/* eslint-disable yola/react/require-default-props */
import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { createFilter } from 'react-select';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import noop from '../../../utils/noop';
import hooks from './hooks';
import { SIZES, APPEARANCES, SEARCHABLE_APPEARANCES } from './constants';
const { useSelect, useInternalComponents, useSelectComponent } = hooks;

const filterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: false,
  stringify: (option) => `${option.label}`,
  matchFrom: 'any',
};

const defaultComponents = {};
const defaultOptions = [];
const defaultOnCreateOption = (label) => ({ label, value: label });
const defaultFilterOptionsFunction = createFilter(filterConfig);

const BaseSelect = forwardRef(
  (
    {
      appearance = APPEARANCES.default,
      clearable = true,
      components = defaultComponents,
      defaultInputValue = '',
      inputValue: controlledInputValue = null,
      disabled = false,
      id = undefined,
      invalid = false,
      name = undefined,
      noResultsText = 'No results found',
      onBlur = noop,
      onChange,
      onKeyDown = noop,
      onCreateOption = defaultOnCreateOption,
      filterOptionsFunction = defaultFilterOptionsFunction,
      onFocus = noop,
      onInputChange = noop,
      placeholder = 'Select item',
      preventOverflow = false,
      size = SIZES.small,
      blurInputOnSelect = true,
      captureMenuScroll = false,
      options = defaultOptions,
      asyncConfig = null,
      // added for SimpleSelect
      value: controlledValue = null,
      defaultValue = undefined,
      className = undefined,
      transparent = false,
      iconComponent = null,
      prefixTextComponent = null,
      defaultMenuIsOpen = false,
      autoFocus = false,
      openMenuOnFocus = false,
      menuIsOpen = null,
      maxMenuHeight = undefined,
      menuWidth = '',
      menuAlignment = 'left',
      shouldMarkSelectedOption = true,
      withDropdownIndicator = true,
      menuPlacement = 'auto',
      noVerticalSpacing = false,
      noHorizontalSpacing = false,
      noSpacingBetweenElements = false,
      isMulti = false,
    },
    ref
  ) => {
    const isAutocomplete = appearance === APPEARANCES.autocomplete;
    const isSearchable = SEARCHABLE_APPEARANCES.includes(appearance);
    const isMobile = bowser.mobile;
    const isTablet = bowser.tablet;
    const isTouchDevice = isMobile || isTablet;
    const SelectComponent = useSelectComponent(isAutocomplete, asyncConfig);
    const selectRef = useRef(null);
    const timerRef = useRef(null);

    useImperativeHandle(
      ref,
      () => ({
        focus() {
          selectRef.current?.focus?.();
        },
        selectOption(option) {
          selectRef.current?.selectOption?.(option);
        },
        /*
         * TODO:
         * Update the solution when all browsers will
         * support the new virtual keyboard API on devices.
         * (Safari, Firefox do not support it now)
         */
        openMenuAutocomplete() {
          timerRef.current = setTimeout(() => {
            selectRef.current?.onMenuOpen?.();
          }, 150);
        },
      }),
      []
    );

    useEffect(
      () => () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      },
      []
    );

    const {
      inputValue,
      isOpen,
      value,
      handleCloseOnScroll,
      handleBlur,
      handleFocus,
      handleChange,
      handleClear,
      handleClose,
      handleInputChange,
      handleOpen,
    } = useSelect({
      defaultInputValue,
      controlledInputValue,
      controlledValue,
      isAutocomplete,
      isSearchable,
      onBlur,
      onFocus,
      onChange,
      onCreateOption,
      onInputChange,
      preventOverflow,
      defaultMenuIsOpen,
      openMenuOnFocus,
      menuIsOpen,
      withDropdownIndicator,
    });

    const customComponents = useInternalComponents(components, {
      withDropdownIndicator,
    });

    // Ensure menu scrolls to selected option when opened.
    // React-Select normally handles scrolling but custom renders
    // or portal placement can prevent it. Force scroll to selected
    // option on open as a fallback.
    useEffect(() => {
      if (!isOpen) return;

      // Slight delay to allow menu DOM to mount
      const timeout = setTimeout(() => {
        try {
          const menuList = document.querySelector(
            '.ws-ui-base-select-menu .ws-ui-base-select__menu-list'
          );
          if (!menuList) return;

          const selected = menuList.querySelector('.ws-ui-base-select__option--is-selected');
          if (selected && typeof selected.scrollIntoView === 'function') {
            selected.scrollIntoView({ block: 'nearest' });
          }
        } catch (err) {
          // ignore DOM errors
        }
      }, 0);

      // eslint-disable-next-line consistent-return
      return () => {
        clearTimeout(timeout);
      };
    }, [isOpen]);

    const classes = classNames('ws-ui-base-select', `ws-ui-base-select--${size}`, className, {
      'ws-ui-base-select--searchable': isSearchable,
      'ws-ui-base-select--is-disabled': disabled,
      'ws-ui-base-select--is-mobile': isMobile,
      'ws-ui-base-select--is-open': isOpen,
      'ws-ui-base-select--transparent': transparent,
      'ws-ui-base-select--with-menu-height-limit': maxMenuHeight,
      'ws-ui-base-select--no-vertical-spacing': noVerticalSpacing,
      'ws-ui-base-select--no-horizontal-spacing': noHorizontalSpacing,
      'ws-ui-base-select--no-spacing-between-elements': noSpacingBetweenElements,
    });

    const showCreateOptionMessage = isAutocomplete ? () => false : undefined;
    const noOptionMessage =
      (isAutocomplete || isSearchable) && !noResultsText ? null : noResultsText;
    const menuPortalTarget = preventOverflow ? document.body : null;

    const isOptionDisabled = (option) => !!option.disabled;

    const handleKeyDown = (event) => {
      onKeyDown?.(event, {
        focusedOption: selectRef.current?.state?.focusedOption,
      });
    };

    return (
      <SelectComponent
        ref={selectRef}
        styles={{
          menu: (styles) => ({
            ...styles,
            ...(menuWidth && { width: menuWidth }),
            left: menuAlignment === 'left' ? 0 : 'auto',
            right: menuAlignment === 'right' ? 0 : 'auto',
          }),
        }}
        blurInputOnSelect={blurInputOnSelect}
        className={classes}
        classNamePrefix="ws-ui-base-select"
        closeMenuOnScroll={handleCloseOnScroll}
        components={customComponents}
        defaultInputValue={defaultInputValue}
        filterOption={filterOptionsFunction}
        inputId={id}
        inputValue={isAutocomplete ? inputValue : undefined}
        invalid={invalid}
        isAutocomplete={isAutocomplete}
        isClearable={clearable}
        isDisabled={disabled}
        isOptionDisabled={isOptionDisabled}
        isSearchable={isSearchable}
        isTouchDevice={isTouchDevice}
        isValidNewOption={showCreateOptionMessage}
        menuIsOpen={isOpen}
        isMulti={isMulti}
        menuPlacement={menuPlacement}
        menuPortalTarget={menuPortalTarget}
        menuShouldScrollIntoView={!preventOverflow}
        name={name}
        noOptionsMessage={() => noOptionMessage}
        onBlur={handleBlur}
        onChange={handleChange}
        onClear={handleClear}
        onFocus={handleFocus}
        onInputChange={handleInputChange}
        onMenuClose={handleClose}
        onMenuOpen={handleOpen}
        onKeyDown={handleKeyDown}
        options={options}
        placeholder={placeholder}
        size={size}
        value={controlledValue || value}
        captureMenuScroll={captureMenuScroll}
        {...(asyncConfig ?? {})}
        defaultValue={defaultValue}
        iconComponent={iconComponent}
        prefixTextComponent={prefixTextComponent}
        defaultMenuIsOpen={defaultMenuIsOpen}
        autoFocus={autoFocus}
        shouldMarkSelectedOption={shouldMarkSelectedOption}
        {...(maxMenuHeight ? { maxMenuHeight } : {})}
      />
    );
  }
);

BaseSelect.displayName = 'BaseSelect';

export default BaseSelect;

export {};
